import React, {useState} from 'react';

import {BioentityAliasInfo} from 'constants/RegistryConstants';
import * as BioentityActions from 'inventory/BioentityActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';
import bufferForUX from 'util/bufferForUX';

import AliasField from './AliasField';

/**
 * Component for managing a registered entity's aliases
 */

interface AliasListProps {
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  fileId: string;
  isWritable: boolean;
  isRegistered: boolean;
}

enum AliasCreationState {
  notCreating,
  editing,
  inFlight,
}

export default function AliasList(props: AliasListProps): JSX.Element {
  const [creationState, setCreationState] = useState<AliasCreationState>(AliasCreationState.notCreating);

  async function updateAlias(oldAlias: string, newAlias: string): Promise<void> {
    await BioentityActions.updateAliasWithDataMods(props.fileId, oldAlias, newAlias, props.isRegistered);
  }

  function deleteAlias(alias: string): void {
    BioentityActions.deleteAlias(props.fileId, alias);
  }

  async function createAlias(alias: string): Promise<void> {
    setCreationState(AliasCreationState.inFlight);
    try {
      await bufferForUX(BioentityActions.addAlias(props.fileId, alias));
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      // Toast has already been shown
    }
    setCreationState(AliasCreationState.notCreating);
  }

  return (
    <div
      className="metadata-aliasList simpleDivTable"
      {...getTestProps({
        component: 'AliasList',
        element: 'root',
      })}
    >
      <div className="simpleDivTable-header">
        <div className="metadata-table-header">Aliases</div>
        {props.isWritable && (
          <AddIcon
            onClick={() => {
              setCreationState(AliasCreationState.editing);
            }}
          />
        )}
      </div>
      {props.bioentityAliasInfos.map((bioentityAliasInfo) => (
        <AliasField
          className="simpleDivTable-row"
          complete={async (newAlias: string) => {
            await updateAlias(bioentityAliasInfo.alias, newAlias);
          }}
          key={bioentityAliasInfo.alias}
          initialAlias={bioentityAliasInfo.alias}
          isWritable={props.isWritable}
          remove={deleteAlias}
        />
      ))}
      {creationState === AliasCreationState.inFlight ? (
        <div className="simpleDivTable-row">
          <HorizontalThrobber />
        </div>
      ) : creationState === AliasCreationState.editing ? (
        <AliasField
          className="simpleDivTable-row"
          cancel={() => {
            setCreationState(AliasCreationState.notCreating);
          }}
          complete={createAlias}
          initialIsBeingEdited={true}
          isWritable={props.isWritable}
        />
      ) : props.bioentityAliasInfos.length === 0 ? (
        <div className="simpleDivTable-row">
          <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
            This entity has no aliases.
          </div>
        </div>
      ) : null}
    </div>
  );
}
